import * as styles from "./AmendmentWeeksCell.css";

import type { JSX } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import {
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from "@frontend/components/atoms/Popover";
import type { ClosureWeekPeriod } from "@frontend/features/projects";
import { Trans, useLingui } from "@lingui/react/macro";

export type AmendmentWeeksCellProps = {
  /** What the column says: a count, or a ratio like "3 / 5". */
  label: string;
  /** The closure weeks behind that number, grouped into runs. */
  periods: ClosureWeekPeriod[];
};

/** dd.mm, the year being the page's own. */
function day(isoDate: string): string {
  const [, month, dayOfMonth] = isoDate.split("-");

  return `${dayOfMonth}.${month}`;
}

/**
 * The week count, and the weeks behind it one click away. Printed in the row,
 * the runs made every line as tall as a paragraph.
 */
export function AmendmentWeeksCell({
  label,
  periods,
}: AmendmentWeeksCellProps): JSX.Element {
  const { t } = useLingui();

  return (
    <span className={styles.cell}>
      {label}

      {periods.length > 0 ? (
        <Popover>
          <PopoverTrigger
            render={
              <Button
                size="sm"
                variant="ghost"
                aria-label={t`Which weeks?`}
                title={t`Which weeks?`}
              />
            }
          >
            <Icon name="CircleQuestionMark" size="sm" />
          </PopoverTrigger>

          <PopoverContent>
            <PopoverTitle>
              <Trans>Closure weeks</Trans>
            </PopoverTitle>

            <ul className={styles.periods}>
              {periods.map((period) => (
                <li key={period.startWeek}>
                  <Trans>Week {period.label}</Trans>{" "}
                  <span className={styles.dates}>
                    {day(period.start)} – {day(period.end)}
                    {null === period.holidayName
                      ? ""
                      : ` · ${period.holidayName}`}
                  </span>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      ) : null}
    </span>
  );
}
